import React from "react";

import { FormattedMessage } from "react-intl";

import ContentLayout from "Components/ContentLayout";
import PageMeta from "Components/PageMeta";
import ProjectsListLayout from "Components/ProjectsListLayout";
import {
  selectFeatureShouldHideProjectOwner,
  selectFeatureUsageBasedBillingEnabled
} from "Reducers/featureFlags/featureFlags.selectors";
import ProjectsSkeleton from "src/organization/common/components/ProjectsSkeleton";
import { useAppSelector } from "Store/hooks";

import { EmptyFilters } from "../EmptyProjects";
import ProjectCard from "../ProjectCard";

import * as S from "./Projects.styles";

import type { Organization } from "@packages/client";
import type { OrganizationProject } from "Api/organizations/projects/OrganizationProjects.types";

type Props = {
  projects: Array<OrganizationProject>;
  loadMore: () => void;
  hasMore: boolean;
  isLoading: boolean;
  organization: Organization;
  resetFilters: () => void;
  gridLayout: boolean;
  shouldShowFilters: boolean;
  isLoadingMore: boolean;
  refetchOrganizationProjectList: () => void;
};

const Projects = (props: Props) => {
  const usageBasedPagesEnabled = useAppSelector(
    selectFeatureUsageBasedBillingEnabled
  );
  const shouldHideProjectOwner = useAppSelector(
    selectFeatureShouldHideProjectOwner
  );

  if (props.isLoading && !props.projects.length)
    return (
      <ContentLayout>
        <ProjectsSkeleton />
      </ContentLayout>
    );

  if (props.projects.length === 0)
    return <EmptyFilters resetFilters={props.resetFilters} />;

  return (
    <>
      <PageMeta title="Projects" />
      <ProjectsListLayout
        as={S.StyledInfiniteScroll}
        threshold={400}
        pageStart={0}
        loadMore={props.loadMore}
        hasMore={props.hasMore && !props.isLoadingMore}
        loader={<S.Loading isGrid={props.gridLayout} />}
        initialLoad={false}
        $isFilterOpen={props.shouldShowFilters}
        className={props.gridLayout ? "grid" : "list"}
      >
        {!props.gridLayout && (
          <S.LabelWrapper>
            <S.ColumnLabel type="name">
              <FormattedMessage id="organization.projects.header.name" />
            </S.ColumnLabel>
            {!shouldHideProjectOwner && (
              <S.ColumnLabel type="owner">
                <FormattedMessage id="organization.projects.header.owner" />
              </S.ColumnLabel>
            )}
            <S.ColumnLabel type="region">
              <FormattedMessage id="organization.projects.header.region" />
            </S.ColumnLabel>
            {!usageBasedPagesEnabled && (
              <S.ColumnLabel type="plan">
                <FormattedMessage id="organization.projects.header.plan" />
              </S.ColumnLabel>
            )}
            <S.ColumnLabel type="id">
              <FormattedMessage id="organization.projects.header.id" />
            </S.ColumnLabel>
          </S.LabelWrapper>
        )}
        {props.projects.map(project => (
          <ProjectCard
            shouldShowProjectOwner={!shouldHideProjectOwner}
            gridLayout={props.gridLayout}
            hasProjectAccess={!!project._links.api}
            key={project.id}
            organizationLabel={props.organization.label}
            organizationName={props.organization.name}
            projectId={project.id}
            projectPlan={project.plan}
            projectRegion={project.region}
            projectStatus={project.status}
            projectSubscriptionId={project.subscription_id}
            projectTitle={project.title}
            projectType={project.type}
            projectCreatedAt={project.created_at}
            refetchOrganizationProjectList={
              props.refetchOrganizationProjectList
            }
          />
        ))}
      </ProjectsListLayout>
    </>
  );
};

export default Projects;
